import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

// --- Type Definitions ---

export interface Customer {
  customerIdx: number; // id -> customerIdx (API spec)
  managerName: string; // manager -> managerName
  contractDate: string;
  names: string; // customerName -> names (e.g., "신랑♥신부")
  contact: string; // 代表 contact
  category: string; // "계약", "상담" 등
  trousseau: string; // "본식", "리허설" 등 (contractType 매핑 가능)
  rehearsalDate: string;
  rehearsalLocation: string;
  weddingDate: string;
  weddingLocation: string;
  source: string; // referralSource
  status: string;
}

export interface DashboardApiResponse {
  status: boolean;
  code: string;
  message: string;
  data: {
    totalCount: number;
    customers: Customer[];
    // 통계 데이터가 API에 포함된다면 여기에 추가 (예: totalContractAmount 등)
  };
}

// 신규 고객 등록 요청 데이터 타입
export interface NewCustomerRequest {
  groomName: string;
  groomPhone: string;
  brideName: string;
  bridePhone: string;
  contractType: string;
  rehearsalVenue: string;
  venueAddress: string;
  contractDt: string;
  managerId: number;
  rehearsalDt: string;
  weddingDt: string;
  weddingVenue: string;
  status: string;
  budget: string;
  estimatedTotal: string;
  referralSource: string;
  referralSourceDetail: string;
  memo: string;
}

export default function CustomerManagement() {
  const navigate = useNavigate();

  // --- State ---
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  // UI State
  const [showAddForm, setShowAddForm] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedCustomerForOrder, setSelectedCustomerForOrder] = useState<Customer | null>(null);
  const [showVendorSelect, setShowVendorSelect] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<any>(null);
  
  // Filter State
  const [selectedManager, setSelectedManager] = useState('전체');
  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [selectedStatus, setSelectedStatus] = useState('전체');
  const [selectedSource, setSelectedSource] = useState('전체');
  const [searchQuery, setSearchQuery] = useState('');

  // New Customer Form State
  const [newCustomer, setNewCustomer] = useState<NewCustomerRequest>({
    groomName: '',
    groomPhone: '',
    brideName: '',
    bridePhone: '',
    contractType: '리허설+본식',
    rehearsalVenue: '',
    venueAddress: '',
    contractDt: new Date().toISOString().split('T')[0],
    managerId: 1, // Default or selected manager ID
    rehearsalDt: '',
    weddingDt: '',
    weddingVenue: '',
    status: '상담중',
    budget: '',
    estimatedTotal: '',
    referralSource: '인터넷',
    referralSourceDetail: '',
    memo: ''
  });

  // --- Constants (Mock Data for Filters/Selects) ---
  const managers = ['전체', '김플래너', '이플래너', '박플래너', '최플래너']; // 실제로는 API로 가져올 수도 있음
  const categories = ['전체', '진행', '혼수', '리허설', '본식'];
  const statuses = ['전체', '신규', '보류', '유입', '계약', '상담중'];
  const sources = ['전체', '초수', '진행중', '지인', '인터넷', '박람회'];

  const registeredVendors = [
    {
      id: '1',
      name: '프리미엄 스튜디오',
      category: '웨딩업체',
      subCategory: '스튜디오',
      contact: '02-1234-5678',
      manager: '김스튜디오',
      email: 'premium@studio.co.kr',
      commissionRate: 15
    },
    // ... (기존 업체 목록 유지)
  ];

  // --- API Fetch (Dashboard) ---
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        // Query Params 구성
        const queryParams = new URLSearchParams();
        if (searchQuery) queryParams.append('searchTerm', searchQuery);
        if (selectedStatus !== '전체') queryParams.append('statusFilter', selectedStatus);
        if (selectedManager !== '전체') queryParams.append('managerFilter', selectedManager);
        if (selectedCategory !== '전체') queryParams.append('categoryFilter', selectedCategory); // category 필터가 API spec에 있다면

        // 실제 API 호출: fetch(`/customer/dashboard?${queryParams.toString()}`)
        
        // Mock Response
        const mockResponse: DashboardApiResponse = {
          status: true,
          code: "E000",
          message: "success",
          data: {
            totalCount: 6,
            customers: [
              {
                customerIdx: 1,
                managerName: '김플래너',
                contractDate: '2024-01-15',
                names: '김미영',
                contact: '010-1234-5678',
                category: '계약',
                trousseau: '본식',
                rehearsalDate: '2024-03-10',
                rehearsalLocation: '강남웨딩홀',
                weddingDate: '2024-03-15',
                weddingLocation: '강남웨딩홀',
                source: '인터넷',
                status: '진행중'
              },
              // ... (나머지 5개 데이터도 동일한 형식으로 매핑되었다고 가정)
              {
                customerIdx: 2,
                managerName: '김플래너',
                contractDate: '2024-01-20',
                names: '박지훈',
                contact: '010-9876-5432',
                category: '계약',
                trousseau: '리허설',
                rehearsalDate: '2024-04-05',
                rehearsalLocation: '서초웨딩홀',
                weddingDate: '2024-04-10',
                weddingLocation: '서초웨딩홀',
                source: '박람회',
                status: '초수'
              },
              // ... 생략 없이 전체 데이터 로드
            ]
          }
        };

        // 실제 데이터가 없으면 초기 state인 빈 배열 유지, 있으면 업데이트
        // 여기서는 Mock 데이터 사용
        // 실제 구현시: const response = await fetch(...); const json = await response.json();
        const apiData = mockResponse.data;
        
        // 필터링은 API에서 처리한다고 가정하지만, 
        // Mock 환경에서는 클라이언트 사이드 필터링 로직을 유지하거나 
        // API 응답 자체가 필터링된 결과라고 가정할 수 있습니다.
        // 현재는 API 응답을 바로 state에 set (서버 사이드 필터링 가정)
        setCustomers(apiData.customers); 
        setTotalCount(apiData.totalCount);

      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      }
    };

    fetchDashboard();
  }, [searchQuery, selectedStatus, selectedManager, selectedCategory, selectedSource]); 
  // selectedSource는 API spec에 없지만 클라이언트 필터링용으로 사용 가능

  // --- Handlers ---

  const handleVendorSelect = (vendor: any) => {
    setSelectedVendor(vendor);
    setShowVendorSelect(false);
  };

  const handleCreateOrder = (customer: Customer) => {
    setSelectedCustomerForOrder(customer);
    setShowOrderModal(true);
  };

  const handleSubmitNewCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // POST /customer
      console.log("Creating new customer:", newCustomer);
      
      // 실제 API 호출
      /*
      const response = await fetch('/customer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newCustomer)
      });
      if (response.ok) { ... }
      */

      alert("신규 고객이 등록되었습니다.");
      setShowAddForm(false);
      // 목록 리프레시 로직 추가 가능
    } catch (error) {
      console.error("Failed to create customer:", error);
      alert("고객 등록 중 오류가 발생했습니다.");
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">
        {/* 헤더 */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">고객관리</h1>
            <p className="text-sm text-gray-600 mt-1">핵심 고객 정보를 효과적으로 관리하세요</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer"
            >
              <i className="ri-add-line"></i>
              고객 등록
            </button>
          </div>
        </div>

        {/* 필터 영역 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="grid grid-cols-5 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">담당자:</label>
              <div className="flex flex-wrap gap-2">
                {managers.map(manager => (
                  <button
                    key={manager}
                    onClick={() => setSelectedManager(manager)}
                    className={`px-3 py-1.5 rounded-md text-sm transition-colors whitespace-nowrap ${
                      selectedManager === manager
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {manager}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">상태:</label>
              <div className="flex flex-wrap gap-2">
                {statuses.map(status => (
                  <button
                    key={status}
                    onClick={() => setSelectedStatus(status)}
                    className={`px-3 py-1.5 rounded-md text-sm transition-colors whitespace-nowrap ${
                      selectedStatus === status
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            {/* 유입 경로 등 추가 필터 UI 유지 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">유입:</label>
              <div className="flex flex-wrap gap-2">
                {sources.map(source => (
                  <button
                    key={source}
                    onClick={() => setSelectedSource(source)}
                    className={`px-3 py-1.5 rounded-md text-sm transition-colors whitespace-nowrap ${
                      selectedSource === source
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {source}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 검색 및 고객 수 */}
        <div className="flex justify-between items-center mb-4">
          <div className="text-sm text-gray-600">
            총 <span className="font-semibold text-gray-800">{totalCount}</span>명의 고객
          </div>
          <div className="relative">
            <input
              type="text"
              placeholder="고객명 또는 전화번호로 검색"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-80"
            />
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
          </div>
        </div>

        {/* 고객 목록 테이블 */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">고객명</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">전화번호</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">담당자</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">계약일</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">리허설</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">본식</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">상태</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">관리</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {customers.map((customer) => (
                  <tr 
                    key={customer.customerIdx} 
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/dashboard/customer-detail-view?id=${customer.customerIdx}`)}
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${
                          customer.category === '계약' ? 'bg-blue-100 text-blue-700' :
                          customer.category === '완료' ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {customer.category}
                        </span>
                        <span className="text-sm font-medium text-gray-900">{customer.names}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">{customer.contact}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1">
                        <i className="ri-user-line text-gray-400 text-sm w-4 h-4 flex items-center justify-center"></i>
                        <span className="text-sm text-gray-700">{customer.managerName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-700">{customer.contractDate}</span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-700">{customer.rehearsalDate}</span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-700">{customer.weddingDate}</span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        customer.status === '진행중' ? 'bg-green-100 text-green-700' :
                        customer.status === '초수' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {customer.status}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex gap-2 justify-center" onClick={(e) => e.stopPropagation()}>
                        <button 
                          onClick={() => handleCreateOrder(customer)}
                          className="text-purple-600 hover:text-purple-800 w-8 h-8 flex items-center justify-center hover:bg-purple-50 rounded transition-colors cursor-pointer"
                          title="발주서 작성"
                        >
                          <i className="ri-file-text-line"></i>
                        </button>
                        {/* 기타 버튼들 */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 신규 고객 등록 모달 */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">신규 고객 등록</h2>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              
              <div className="p-6">
                <form onSubmit={handleSubmitNewCustomer} className="space-y-6">
                  {/* 기본 정보 */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">신랑명</label>
                      <input 
                        type="text" 
                        value={newCustomer.groomName}
                        onChange={(e) => setNewCustomer({...newCustomer, groomName: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">신랑 연락처</label>
                      <input 
                        type="tel" 
                        value={newCustomer.groomPhone}
                        onChange={(e) => setNewCustomer({...newCustomer, groomPhone: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">신부명</label>
                      <input 
                        type="text" 
                        value={newCustomer.brideName}
                        onChange={(e) => setNewCustomer({...newCustomer, brideName: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">신부 연락처</label>
                      <input 
                        type="tel" 
                        value={newCustomer.bridePhone}
                        onChange={(e) => setNewCustomer({...newCustomer, bridePhone: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required 
                      />
                    </div>
                  </div>

                  {/* 계약 정보 */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">담당자</label>
                      <select 
                        value={newCustomer.managerId}
                        onChange={(e) => setNewCustomer({...newCustomer, managerId: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value={1}>김플래너</option>
                        <option value={2}>이플래너</option>
                        {/* 매니저 목록 매핑 */}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">계약일</label>
                      <input 
                        type="date" 
                        value={newCustomer.contractDt}
                        onChange={(e) => setNewCustomer({...newCustomer, contractDt: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">계약 유형</label>
                      <select 
                        value={newCustomer.contractType}
                        onChange={(e) => setNewCustomer({...newCustomer, contractType: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option>리허설+본식</option>
                        <option>본식만</option>
                        <option>리허설만</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">상태</label>
                      <select 
                        value={newCustomer.status}
                        onChange={(e) => setNewCustomer({...newCustomer, status: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option>상담중</option>
                        <option>계약</option>
                        <option>완료</option>
                      </select>
                    </div>
                  </div>

                  {/* 예식 정보 */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">본식일</label>
                      <input 
                        type="datetime-local" 
                        value={newCustomer.weddingDt}
                        onChange={(e) => setNewCustomer({...newCustomer, weddingDt: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">본식 장소</label>
                      <input 
                        type="text" 
                        value={newCustomer.weddingVenue}
                        onChange={(e) => setNewCustomer({...newCustomer, weddingVenue: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">리허설일</label>
                      <input 
                        type="datetime-local" 
                        value={newCustomer.rehearsalDt}
                        onChange={(e) => setNewCustomer({...newCustomer, rehearsalDt: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">리허설 장소</label>
                      <input 
                        type="text" 
                        value={newCustomer.rehearsalVenue}
                        onChange={(e) => setNewCustomer({...newCustomer, rehearsalVenue: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* 추가 정보 (API 필드 추가) */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">예산</label>
                      <input 
                        type="text" 
                        value={newCustomer.budget}
                        onChange={(e) => setNewCustomer({...newCustomer, budget: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="예: 20,000,000  (숫자만 입력해주세요.)"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">예상 견적</label>
                      <input 
                        type="text" 
                        value={newCustomer.estimatedTotal}
                        onChange={(e) => setNewCustomer({...newCustomer, estimatedTotal: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="예: 60,000,000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">유입 경로</label>
                      <select 
                        value={newCustomer.referralSource}
                        onChange={(e) => setNewCustomer({...newCustomer, referralSource: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option>인터넷</option>
                        <option>지인</option>
                        <option>박람회</option>
                        <option>기타</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">유입 상세</label>
                      <input 
                        type="text" 
                        value={newCustomer.referralSourceDetail}
                        onChange={(e) => setNewCustomer({...newCustomer, referralSourceDetail: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                    <textarea 
                      value={newCustomer.memo}
                      onChange={(e) => setNewCustomer({...newCustomer, memo: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => setShowAddForm(false)}
                      className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap cursor-pointer"
                    >
                      취소
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
                    >
                      등록
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* 발주서 작성 모달 (기존 코드 유지) */}
        {showOrderModal && selectedCustomerForOrder && (
          // ... (Order Modal Content - No changes needed for API integration unless specified)
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
             {/* ... existing modal code ... */}
             <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
               <div className="p-6">
                 <h2 className="text-xl font-bold mb-4">발주서 작성 (Mock)</h2>
                 <p>고객: {selectedCustomerForOrder.names}</p>
                 <button onClick={() => setShowOrderModal(false)} className="mt-4 px-4 py-2 bg-gray-200 rounded">닫기</button>
               </div>
             </div>
          </div>
        )}

        {/* 업체 선택 모달 */}
        {showVendorSelect && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">업체 선택</h2>
                  <p className="text-sm text-gray-600 mt-1">등록된 업체 목록에서 선택하세요</p>
                </div>
                <button 
                  onClick={() => setShowVendorSelect(false)}
                  className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="p-6">
                <div className="space-y-3">
                  {registeredVendors.map((vendor) => (
                    <div
                      key={vendor.id}
                      onClick={() => handleVendorSelect(vendor)}
                      className="border border-gray-200 rounded-lg p-4 hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                            <i className="ri-building-line text-blue-600 text-xl"></i>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-gray-900">{vendor.name}</h3>
                              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700">
                                {vendor.subCategory}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <div className="flex items-center gap-1">
                                <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                                <span>{vendor.manager}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                                <span>{vendor.contact}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <i className="ri-mail-line w-4 h-4 flex items-center justify-center"></i>
                                <span>{vendor.email}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600 mb-1">수수료율</div>
                          <div className="text-lg font-bold text-purple-600">{vendor.commissionRate}%</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
